import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createMemoryRouter, Outlet, useLocation, useNavigate } from 'react-router';
import { APP_CHILDREN } from '../routes.tsx';
import { queryClient } from '../lib/queryClient.ts';
import { AuthProvider } from '../lib/auth.tsx';
import { TabsBar } from './TabsBar.tsx';
import { activeTab, type PaneKey, type PaneTabs } from './tabs.ts';

export interface PaneProps {
  state: PaneTabs;
  onSelect: (id: string) => void; onClose: (id: string) => void; onNew: () => void;
  onMove: (from: PaneKey, id: string, to: PaneKey, index?: number) => void;
  onCloseOthers?: () => void;
  onLocation: (path: string, title: string | null) => void;
  onClosePane: () => void;
}

/**
 * 화면 분할 (CR-011): 오른쪽 두 번째 화면.
 * react-router 는 Router 중첩을 금지하므로 별도 React 루트에 메모리 라우터를 띄운다.
 * 쿼리 캐시·인증 컨텍스트는 주 화면과 공유하고, 탭 상태는 props 로 받아 바뀔 때마다 다시 그린다.
 */
export function SplitPane(props: PaneProps) {
  const host = useRef<HTMLDivElement>(null);
  const rootRef = useRef<Root | null>(null);
  const routerRef = useRef<ReturnType<typeof createMemoryRouter> | null>(null);
  const latest = useRef(props); latest.current = props;
  useEffect(() => {
    const el = host.current; if (!el) return;
    const initial = activeTab(props.state)?.path ?? '/';
    routerRef.current = createMemoryRouter([{ path: '/', Component: PaneLayoutHost, children: APP_CHILDREN }], { initialEntries: [initial] });
    rootRef.current = createRoot(el);
    return () => { const r = rootRef.current; rootRef.current = null; setTimeout(() => r?.unmount(), 0); };
  }, []); // eslint-disable-line
  useEffect(() => {
    if (!rootRef.current || !routerRef.current) return;
    rootRef.current.render(<QueryClientProvider client={queryClient}><AuthProvider><PaneCtx.Provider value={props}><RouterProvider router={routerRef.current} /></PaneCtx.Provider></AuthProvider></QueryClientProvider>);
  }, [props]);
  return <div ref={host} className="h-full min-h-0" data-pane="right" />;
}

const PaneCtx = createContext<PaneProps | null>(null);

function PaneLayoutHost() {
  const p = useContext(PaneCtx)!;
  const loc = useLocation();
  const navigate = useNavigate();
  const host = useRef<HTMLDivElement>(null);
  const [, force] = useState(0);
  const cur = loc.pathname + loc.search;
  const want = activeTab(p.state)?.path ?? null;
  // 탭 전환·이동으로 활성 경로가 바뀌면 그 경로로 이동
  useEffect(() => { if (want && want !== cur) navigate(want); }, [want]); // eslint-disable-line
  // 화면 이동 후 제목(h1) 을 읽어 활성 탭 갱신
  useEffect(() => { const t = setTimeout(() => { p.onLocation(cur, null); force((n) => n + 1); }, 60); return () => clearTimeout(t); }, [cur]); // eslint-disable-line
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-stretch bg-slate-200 border-b border-slate-300">
        <div className="flex-1 min-w-0"><TabsBar pane="right" state={p.state} onSelect={p.onSelect} onClose={p.onClose} onNew={p.onNew} onMove={p.onMove} onCloseOthers={p.onCloseOthers} canMove otherLabel="왼쪽" /></div>
        <div className="flex items-center gap-1 px-1 text-[12px] shrink-0">
          <button type="button" className="px-1 text-slate-600 hover:text-accent-strong" title="뒤로" onClick={() => navigate(-1)}>←</button>
          <button type="button" className="px-1 text-slate-600 hover:text-accent-strong" title="앞으로" onClick={() => navigate(1)}>→</button>
          <button type="button" className="px-1 text-slate-600 hover:text-red-700" title="분할 닫기" onClick={p.onClosePane}>✕</button>
        </div>
      </div>
      <div ref={host} className="flex-1 min-h-0 overflow-auto p-2">{p.state.tabs.length ? <Outlet /> : <div className="text-sm text-slate-500 p-4">탭이 없습니다. + 로 새 탭을 열거나 왼쪽 탭을 끌어오세요.</div>}</div>
    </div>
  );
}
